import { LanguageManager } from 'core/language/LanguageManager';
import { Account, AuthenticationManager } from 'core';
import { ActorMenuModel, DefaultActorMenuModel } from './ActorMenu';
import { Toggleable } from '../AppMenuBar';
import { MainPageModel } from 'pages/MainPage/MainPageModel';
import { ChangePassword } from '../AppHeader/ChangePassword';
import { DefaultChangePasswordModel } from '../AppHeader/ChangePasswordModel';
import { toast } from 'react-toastify';

import _ from 'lodash';
import i18n from 'i18n';
import { FireableUpdateEventListener, UpdateEventListener } from 'utils/UpdateEventListener';
import { DefaultSudoModel, SudoModel } from '../AppHeader/SudoModel';
import { Sudo } from './Sudo';

export interface AppHeaderModel {

  readonly state: AppHeaderState;

  readonly language: string;
  readonly account: Account | null;
  readonly actorMenuModel: ActorMenuModel | null;
  readonly event: UpdateEventListener<AppHeaderModel>;

  logout (): void;
  toggle (): void;
  changeLanguage (): void;
  showHideAccoutMenu (): void;
  changePassword (): void;
  hideModal (): void;
  sudo (): void;
  showChangeLanguageAlertModel (): void;
}

export type AppHeaderProps = {

  readonly model: AppHeaderModel;
};

export type AppHeaderState = {
  menuShown: boolean;
  modalData: any;
  loading: boolean;
};

export class EmptyAppHeaderModel implements AppHeaderModel {
  modalData: any;
  event: FireableUpdateEventListener<AppHeaderModel>;
  constructor () {
    this.event = new FireableUpdateEventListener<AppHeaderModel>();
  }

  get language (): string {
    return '';
  }

  get account (): Account | null {
    return null;
  }

  get actorMenuModel (): ActorMenuModel | null {
    return null;
  }

  get state (): AppHeaderState {
    return {
      loading: false,
      menuShown: false,
      modalData: this.modalData
    };
  }

  toggle () {
    // This is intentional
  }

  logout () {
    // This is intentional
  }

  changeLanguage () {
    // This is intentional
  }

  showHideAccoutMenu () {
    // This is intentional
  }

  changePassword () {
    // This is intentional
  }

  hideModal () {
    // This is intentional
  }

  sudo () {
    // This is intentional
  }

  showChangeLanguageAlertModel () {
    // This is intentional
  }
}

export class DefaultAppHeaderModel implements AppHeaderModel {
  accountMenuShown: boolean;
  toggleable: Toggleable;
  menuModel: ActorMenuModel | null;
  sudoModel: SudoModel;
  languageManager: LanguageManager;
  authenticationManager: AuthenticationManager;
  mainPageModel: MainPageModel;
  modalData: any;
  event: FireableUpdateEventListener<AppHeaderModel>;
  loading: boolean = false;

  constructor (authenticationManager: AuthenticationManager, languageManager: LanguageManager, toggleable: Toggleable, mainPageModel: MainPageModel) {
    this.menuModel = null;
    this.toggleable = toggleable;
    this.accountMenuShown = false;
    this.languageManager = languageManager;
    this.authenticationManager = authenticationManager;
    this.mainPageModel = mainPageModel;
    this.event = new FireableUpdateEventListener<AppHeaderModel>();
    this.sudoModel = new DefaultSudoModel(this.authenticationManager);
    this.sudoModel.event.add(event => {
      if (_.get(event, 'state.isSubmitted', false)) {
        toast.success(i18n.t<string>('sudo.message.success'));
        this.hideModal();
        _.delay(() => window.location.href = '/', 1000);
      }
    });
  }

  get state (): AppHeaderState {
    return {
      menuShown: this.accountMenuShown,
      modalData: this.modalData,
      loading: this.loading
    };
  }

  get account (): Account | null {
    return this.authenticationManager.account;
  }

  get language (): string {
    return _.first(_.without(this.languageManager.availableLanguages, this.languageManager.currentLanguage)) || '';
  }

  get actorMenuModel (): ActorMenuModel | null {
    if (this.menuModel === null) {
      if (this.authenticationManager.actor !== null) {
        this.menuModel = new DefaultActorMenuModel(this.authenticationManager.actor, this.authenticationManager, this.mainPageModel);
      }
    }
    return this.menuModel;
  }

  toggle () {
    this.toggleable.expandOrCollapse();
  }

  logout () {
    this.menuModel = null;
    this.accountMenuShown = false;
    this.authenticationManager.logout();
  }

  changeLanguage () {
    this.loading = true;
    this.event.fireEvent(this);
    this.languageManager.setCurrentLanguageAndReloadPage(this.language);
  }

  showHideAccoutMenu () {
    this.accountMenuShown = !this.accountMenuShown;
  }

  showChangeLanguageAlertModel () {
    this.modalData = {
      title: i18n.t<string>('common.warning'),
      message: i18n.t<string>('appHeader.changeLanguage.message'),
      confirmBtnData: {
        callback: this.changeLanguage.bind(this),
        title: i18n.t<string>('common.buttons.ok')
      }
    };
    this.event.fireEvent(this);
  }

  sudo = () => {
    this.modalData = {
      modalClassName: 'app-header-modal',
      title: i18n.t<string>('appHeader.sudo.title'),
      fullScreen: true,
      component: Sudo,
      confirmBtnData: {
        callback: this.sudoModel.sudo.bind(this.sudoModel),
        title: i18n.t<string>('common.buttons.submit')
      },
      componentProps: {
        model: this.sudoModel
      }
    };
    this.event.fireEvent(this);
  }

  changePassword = () => {
    const passwordModel = new DefaultChangePasswordModel(this.authenticationManager);
    passwordModel.event.add(event => {
      if (_.get(event, 'state.isSubmitted', false)) {
        toast.success(i18n.t<string>('changePasswords.message.reload'));
        this.hideModal();
        _.delay(this.authenticationManager.logout.bind(this.authenticationManager), 3000);
      }
    });

    this.modalData = {
      title: i18n.t<string>('changePasswords.title.change'),
      fullScreen: true,
      component: ChangePassword,
      confirmBtnData: {
        callback: passwordModel.submit.bind(passwordModel),
        title: i18n.t<string>('common.buttons.submit')
      },
      componentProps: {
        model: passwordModel
      }
    };
    this.event.fireEvent(this);
  }

  hideModal = () => {
    this.modalData = undefined;
    this.event.fireEvent(this);
  }
}
